// V36 — Mobile-first "My Profile" menu screen (BookMyTutor parity).
// The bottom-nav "Me" tab now lands here. This is a NAV screen, not an editor —
// it routes the tutor to the full TutorProfile editor (Edit Profile), policies,
// FAQs, etc., matching the reference list-item layout.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ChevronRight, User, BookOpen, ShieldCheck, Star, FileText, XCircle,
    HelpCircle, Info, Instagram, Youtube, Trash2, LogOut, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useTutorAuth } from "../context/TutorAuthContext";
import TutorMobileLayout from "../components/tutor/TutorMobileLayout";
import TutorSelfDeleteCard from "../components/tutor/TutorSelfDeleteCard";

export default function TutorMe() {
    const { tutor, logout } = useTutorAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [showDeletePanel, setShowDeletePanel] = useState(false);

    const handleLogout = async () => {
        if (busy) return;
        if (!window.confirm("Log out of the tutor portal?")) return;
        setBusy(true);
        try {
            await logout();
            toast.success("Logged out");
            navigate("/tutor/login", { replace: true });
        } catch {
            toast.error("Logout failed");
        } finally {
            setBusy(false);
        }
    };

    const initials = (tutor?.name || "")
        .split(/\s+/).filter(Boolean).slice(0, 2)
        .map((s) => s[0]?.toUpperCase()).join("") || "A";

    const items = [
        { id: "your-id", icon: User, label: "Your ID", value: tutor?.application_id || tutor?.id || "—", muted: true },
        { id: "view-profile", icon: User, label: "View Profile", to: "/tutor/profile" },
        { id: "edit-profile", icon: User, label: "Edit Profile", to: "/tutor/profile" },
        { id: "courses-i-teach", icon: BookOpen, label: "Courses I teach", to: "/tutor/profile#academics" },
        { id: "policies", icon: ShieldCheck, label: "Policies", to: "/policies/tutor" },
        { id: "rate-app", icon: Star, label: "Rate our App", action: () => toast.info("Rating coming soon") },
        { id: "terms", icon: FileText, label: "Terms & Conditions", to: "/policies/terms" },
        { id: "refund", icon: XCircle, label: "Refund & Cancellation", to: "/policies/refund" },
        { id: "faqs", icon: HelpCircle, label: "Frequently Asked Questions", to: "/tutor/faqs" },
        { id: "about", icon: Info, label: "About", to: "/about" },
    ];

    return (
        <TutorMobileLayout title="My Profile">
            {/* Identity header */}
            <section className="-mx-4 -mt-4 mb-4 bg-gradient-to-br from-[#E03A2F] via-[#E84A1B] to-[#F35F1A] text-white px-5 pt-6 pb-8 relative overflow-hidden" data-testid="tutor-me-identity">
                <div className="absolute -top-12 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
                <div className="flex items-center gap-3 relative">
                    <div className="w-16 h-16 rounded-full bg-white text-[#E03A2F] flex items-center justify-center text-2xl font-bold shrink-0 overflow-hidden">
                        {tutor?.profile_picture_url ? (
                            <img src={tutor.profile_picture_url} alt="" className="w-full h-full object-cover" />
                        ) : initials.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-bold text-xl leading-tight truncate" data-testid="tutor-me-name">{tutor?.name || "Tutor"}</div>
                        <div className="text-[12px] opacity-90 mt-0.5 truncate" data-testid="tutor-me-email">{tutor?.email}</div>
                        {tutor?.phone && <div className="text-[12px] opacity-90 mt-0.5" data-testid="tutor-me-phone">{tutor.phone}</div>}
                    </div>
                </div>
            </section>

            {/* Menu list */}
            <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm mb-4" data-testid="tutor-me-menu">
                {items.map((it, idx) => {
                    const Icon = it.icon;
                    const inner = (
                        <>
                            <div className="w-10 h-10 rounded-full ring-1 ring-slate-200 flex items-center justify-center shrink-0">
                                <Icon className="w-5 h-5 text-slate-700" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[15px] font-semibold text-[#0F172A] truncate">{it.label}</div>
                                {it.value && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{it.value}</div>}
                            </div>
                            {!it.muted && <ChevronRight className="w-5 h-5 text-slate-400" />}
                        </>
                    );
                    const cls = `flex items-center gap-3 px-4 py-3.5 min-h-[56px] active:bg-slate-50 transition ${idx > 0 ? "border-t border-slate-100" : ""}`;
                    if (it.to) return <Link key={it.id} to={it.to} className={cls} data-testid={`tutor-me-${it.id}`}>{inner}</Link>;
                    if (it.action) return <button key={it.id} onClick={it.action} className={`${cls} w-full text-left`} data-testid={`tutor-me-${it.id}`}>{inner}</button>;
                    return <div key={it.id} className={cls} data-testid={`tutor-me-${it.id}`}>{inner}</div>;
                })}

                {/* Follow us row */}
                <div className="flex items-center gap-3 px-4 py-3.5 min-h-[56px] border-t border-slate-100" data-testid="tutor-me-follow">
                    <div className="w-10 h-10 rounded-full ring-1 ring-slate-200 flex items-center justify-center shrink-0">
                        <User className="w-5 h-5 text-slate-700" />
                    </div>
                    <div className="flex-1 text-[15px] font-semibold text-[#0F172A]">Follow us</div>
                    <a href="https://instagram.com/anandtutorials" target="_blank" rel="noreferrer"
                        className="w-9 h-9 rounded-lg bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 text-white flex items-center justify-center active:scale-95 transition"
                        data-testid="tutor-me-instagram">
                        <Instagram className="w-5 h-5" />
                    </a>
                    <a href="https://youtube.com/@anandtutorials" target="_blank" rel="noreferrer"
                        className="w-9 h-9 rounded-lg bg-red-600 text-white flex items-center justify-center active:scale-95 transition"
                        data-testid="tutor-me-youtube">
                        <Youtube className="w-5 h-5" />
                    </a>
                </div>
            </section>

            {/* Delete + Logout */}
            <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm mb-4">
                <button
                    onClick={() => setShowDeletePanel((v) => !v)}
                    className="flex items-center gap-3 px-4 py-3.5 min-h-[56px] w-full text-left active:bg-slate-50 transition"
                    data-testid="tutor-me-delete-account"
                >
                    <div className="w-10 h-10 rounded-full ring-1 ring-slate-200 flex items-center justify-center shrink-0">
                        <Trash2 className="w-5 h-5 text-slate-700" />
                    </div>
                    <div className="flex-1 text-[15px] font-semibold text-[#0F172A]">Delete Account</div>
                    <ChevronRight className={`w-5 h-5 text-slate-400 transition ${showDeletePanel ? "rotate-90" : ""}`} />
                </button>
                {showDeletePanel && (
                    <div className="px-4 pb-4 border-t border-slate-100" data-testid="tutor-me-delete-panel">
                        <TutorSelfDeleteCard onDeleted={() => navigate("/tutor/login", { replace: true })} />
                    </div>
                )}

                <button
                    onClick={handleLogout}
                    disabled={busy}
                    className="flex items-center gap-3 px-4 py-3.5 min-h-[56px] w-full text-left border-t border-slate-100 active:bg-rose-50 transition"
                    data-testid="tutor-me-logout"
                >
                    <div className="w-10 h-10 rounded-full ring-1 ring-rose-200 flex items-center justify-center shrink-0 bg-rose-50">
                        {busy ? <Loader2 className="w-5 h-5 text-rose-600 animate-spin" /> : <LogOut className="w-5 h-5 text-rose-600" />}
                    </div>
                    <div className="flex-1 text-[15px] font-bold text-rose-600">Logout</div>
                    <ChevronRight className="w-5 h-5 text-rose-400" />
                </button>
            </section>

            <p className="text-center text-[10px] text-slate-400 mt-4">
                Anand Tutorials · Tutor Portal · v{process.env.REACT_APP_VERSION || "1.0"}
            </p>
        </TutorMobileLayout>
    );
}
